import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, DatePicker, Form, Input, Modal, Row, Space, Table, TreeSelect } from "antd";
import { isMobile } from "react-device-detect";
import { createCustomerCareLog, updateCustomerCareLog } from "@/redux/actions/customerCareLogActions";
import { fetchCustomer } from "@/redux/actions/customerActions";
import { _newLine, makeTree, renderCustomerServiceStatus } from "@/utils/helpers";
import { SaveOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";

const { TextArea } = Input;

const CustomerCareForm = ({
    customerId,
    visible,
    onCancel,
    reloadData,
    editingItem
}) => {

    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isCustomerLoading, setIsCustomerLoading] = useState(false);

    // Lấy customer từ redux
    const currentCustomer = useSelector(
        state => state.customerReducer?.detail
    );

    const filterData = useSelector(state => state.filterReducer.list);
    const customerCareCategories = filterData?.customer_care_categories;
    const listParentTree = makeTree(customerCareCategories || [], 0);

    // Auto fetch customer
    useEffect(() => {
        if (visible && customerId) {
            setIsCustomerLoading(true);

            dispatch(fetchCustomer(customerId))
                .finally(() => {
                    setIsCustomerLoading(false);
                });
        }
    }, [visible, customerId, dispatch]);

    // Form submit
    const handleSubmit = async () => {
        if (!customerId) return;

        setIsLoading(true);

        try {
            const values = await form.validateFields();

            const payload = {
                ...values,
                called_at: values.called_at
                    ? values.called_at.format("YYYY-MM-DD HH:mm:ss")
                    : null
            };

            if (editingItem) {
                await dispatch(
                    updateCustomerCareLog(customerId, editingItem.id, payload)
                );
            } else {
                await dispatch(
                    createCustomerCareLog(customerId, payload)
                );
            }

            reloadData();
            onClose();

        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const onClose = () => {
        form.resetFields();
        onCancel();
    };

    // Reset khi mở modal
    useEffect(() => {
        if (visible && !editingItem) {
            form.resetFields();
        }
    }, [visible, editingItem, form]);

    // Edit mode
    useEffect(() => {
        if (!editingItem) return;

        const categoryIds =
            editingItem.category_ids ||
            editingItem.categories?.map(c => c.id) ||
            [];

        form.setFieldsValue({
            called_at: editingItem.called_at
                ? moment(editingItem.called_at)
                : null,
            category_ids: categoryIds,
            customer_feedback:
                _newLine(editingItem.customer_feedback) || ""
        });

    }, [editingItem, form]);

    // Table data
    const dataSource = (currentCustomer?.active_purchased_services || []).map(item => ({
        key: item.id,
        serviceName: item.service?.name,
        serviceCode: item.service?.code,
        amount: item.amount,
        selectedAt: item.selected_at,
        raw: item,
        status: item.status,
    }));

    const columns = [
        {
            title: "Dịch vụ",
            dataIndex: "serviceName",
            render: (text, record) => (
                <div>
                    <b>{text}</b>
                    <div style={{ fontSize: 12, color: "#888" }}>
                        Mã: {record?.serviceCode} ·
                        Giá: {record?.amount?.toLocaleString()} đ
                    </div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                        Ngày chốt:{" "}
                        {record?.selectedAt
                            ? moment(record.selectedAt).format("DD/MM/YYYY")
                            : "--"}
                    </div>
                </div>
            )
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            align: "right",
            width: 120,
            render: status => renderCustomerServiceStatus(status)
        }
    ];

    return (
        <Modal
            title={editingItem ? "Cập nhật CSKH" : "Thêm CSKH"}
            open={visible}
            onCancel={onClose}
            confirmLoading={isLoading}
            width={isMobile ? "100%" : "50%"}
            destroyOnClose
            maskClosable={false}
            footer={
                <Space>
                    <Button size="large" onClick={onClose}>
                        Huỷ
                    </Button>
                    <Button
                        loading={isLoading}
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                    >
                        Lưu
                    </Button>
                </Space>
            }
        >
            <Form layout="vertical" form={form}>
                <Row gutter={16}>
                    <Col span={24}>
                        <h6>
                            <UserOutlined /> Khách hàng:&nbsp;
                            <span className="color-primary">
                                {currentCustomer?.full_name} - {currentCustomer?.code}
                            </span>
                        </h6>
                    </Col>
                </Row>

                <Table
                    title={() => <strong>Dịch vụ khách đang sử dụng</strong>}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    loading={isCustomerLoading}
                    size="small"
                />

                <Form.Item
                    name="called_at"
                    label="Ngày gọi"
                    rules={[{ required: true, message: "Chọn ngày gọi" }]}
                >
                    <DatePicker
                        showTime
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD HH:mm"
                    />
                </Form.Item>

                <Form.Item
                    name="category_ids"
                    label="Nội dung CSKH"
                    rules={[{ required: true, message: "Chọn ít nhất 1 danh mục" }]}
                >
                    <TreeSelect
                        treeData={listParentTree || []}
                        multiple
                        allowClear
                        treeDefaultExpandAll
                        style={{ width: "100%" }}
                    />
                </Form.Item>

                <Form.Item
                    name="customer_feedback"
                    label="Phản hồi khách"
                    rules={[{ required: true, message: "Bắt buộc nhập phản hồi khách" }]}
                >
                    <TextArea rows={6} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CustomerCareForm;